<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import PMessage from 'primevue/message'
import PSkeleton from 'primevue/skeleton'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import CDashboardModuleCard, {
  type DiagnosticCardData,
  type ModuleCardData
} from './CDashboardModuleCard.vue'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom'
import { useAuthStore } from '@/presentation/stores/auth.ts'
import { useSchoolStore } from '@/presentation/stores/school/school.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import { useNabooContent } from '@/assets/composables/useNabooContent.ts'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'

// PROPS
const props = defineProps<{
  schoolId: number
  classroomId: number
}>()

// COMPOSABLES
const router = useRouter()
const { openQuizVersionDialog } = useNabooContent()

// STORES
const dashboardClassroomStore = useDashboardClassroomStore()
const schoolStore = useSchoolStore()
const { isAdmin } = storeToRefs(useAuthStore())

// STORE REFS
const {
  dashboardClassroomAssignments,
  dashboardClassroomDiagnosticState,
  dashboardClassroomDiagnosticsByType,
  contextSchoolId,
  contextPeriodId,
  dashboardClassroomStoreLoading,
  dashboardClassroomStoreError,
} = storeToRefs(dashboardClassroomStore)

// LOCAL STATE
const localLoading = ref(false)
const localError = ref('')

// COMPUTED
const hasError = computed(
  () =>
    (dashboardClassroomStoreError.value && dashboardClassroomStoreError.value.hasError) ||
    localError.value !== ''
)

const errorMessage = computed(
  () =>
    localError.value ||
    (dashboardClassroomStoreError.value?.hasError ? dashboardClassroomStoreError.value.message : '')
)

const isDataLoading = computed(
  () => dashboardClassroomStoreLoading.value.fetchClassroomAssignments || localLoading.value
)

const classroomAssignment = computed(() => {
  return dashboardClassroomAssignments.value
})

// État des diagnostics basé sur les vraies données

const diagnosticT1Status = computed(() => {
  // Si aucun module n'est assigné, le diagnostic est désactivé
  if (!classroomAssignment.value || classroomAssignment.value.assignedModules.length === 0) {
    return 'disabled'
  }

  // Si pas d'état de diagnostic chargé, on attend
  if (!dashboardClassroomDiagnosticState.value) return 'pending'

  // Si le diagnostic existe déjà, il est complété
  if (dashboardClassroomDiagnosticState.value.isDiagnosticStartExists) return 'completed'

  return 'pending'
})

const diagnosticT2Status = computed(() => {
  if (!dashboardClassroomDiagnosticState.value) return 'disabled'

  const t1Complete = dashboardClassroomDiagnosticState.value.isDiagnosticStartExists
  const t2Exists = dashboardClassroomDiagnosticState.value.isDiagnosticEndExists

  if (t2Exists) return 'completed'
  return t1Complete ? 'pending' : 'disabled'
})

const moduleStatus = computed(
  () =>
    (moduleId: number): 'pending' | 'active' | 'completed' | 'disabled' => {
      const module = classroomAssignment.value?.assignedModules.find(
        (m) => m.learningModuleId === moduleId
      )
      if (!module) return 'disabled'

      const t1Complete = dashboardClassroomDiagnosticState.value?.isDiagnosticStartExists ?? false

      if (!t1Complete) return 'disabled' // Modules disabled tant que T1 n'est pas publié

      return module.visible ? 'completed' : 'pending' // pending = peut être publié
    }
)

// Cartes affichées (diagnostics + modules)
const cards = computed(() => {
  const result: (DiagnosticCardData | ModuleCardData)[] = []

  // Diagnostic T1
  result.push({
    id: 'D1',
    title: QuizType.label(QuizType.Values.DIAG_START),
    type: 'diagnostic',
    status: diagnosticT1Status.value,
    courseName: 'Andromède',
    description:
      'Évalue les connaissances de vos élèves sur toutes les thématiques abordées dans les modules sélectionnés.'
  })

  // Modules (toujours affichés, mais avec état différent selon D1)
  if (classroomAssignment.value) {
    // Séparer les modules visibles et non visibles
    const visibleModules = classroomAssignment.value.assignedModules.filter((m) => m.visible)
    const hiddenModules = classroomAssignment.value.assignedModules.filter((m) => !m.visible)

    // Trier les modules visibles par order
    visibleModules.sort((a, b) => a.order - b.order)

    // Trier les modules non visibles par learningCourseOrder, puis learningModuleOrder
    hiddenModules.sort((a, b) => {
      if (a.learningCourseOrder !== b.learningCourseOrder) {
        return a.learningCourseOrder - b.learningCourseOrder
      }
      return a.learningModuleOrder - b.learningModuleOrder
    })

    // Créer les cartes : d'abord les visibles triés, puis les non visibles
    const sortedModules = [...visibleModules, ...hiddenModules]
    const moduleCards = sortedModules.map((module) => ({
      id: module.learningModuleId,
      title: module.learningModuleTitle,
      type: 'module' as const,
      status: moduleStatus.value(module.learningModuleId),
      visible: module.visible
    })) as ModuleCardData[]
    result.push(...moduleCards)
  }

  // Diagnostic D2
  result.push({
    id: 'D2',
    title: QuizType.label(QuizType.Values.DIAG_END),
    type: 'diagnostic',
    status: diagnosticT2Status.value,
    courseName: 'Andromède',
    description:
      "Évalue les connaissances des élèves sur tous les modules réalisés pendant l'année."
  })

  return result
})

// METHODS
async function handleActivateDiagnostic(diagnosticId: string) {
  try {
    localLoading.value = true
    localError.value = ''

    let result
    if (diagnosticId === 'D1') {
      result = await dashboardClassroomStore.triggerDashboardDiagnosticStart()
    } else if (diagnosticId === 'D2') {
      result = await dashboardClassroomStore.triggerDashboardDiagnosticEnd()
    }

    if (result) {
      // Recharger les données après déclenchement
      await Promise.all([
        dashboardClassroomStore.fetchDashboardClassroomAssignments(true),
        dashboardClassroomStore.fetchDashboardClassroomDiagnosticState()
      ])
    }
  } catch (err) {
    // Détecter les erreurs liées à une version de quiz manquante
    const errorMessage = err instanceof Error ? err.message : String(err)
    if (
      errorMessage.includes('missing quiz version') ||
      errorMessage.includes('quiz version') ||
      errorMessage.includes('AssignmentForClassroom')
    ) {
      localError.value = 'Vous devez d\'abord créer une version du quiz avant de le publier'
    } else {
      localError.value = 'Erreur lors du déclenchement du diagnostic'
    }
    console.error('Error triggering diagnostic:', err)
  } finally {
    localLoading.value = false
  }
}

async function handleActivateModule(moduleId: number) {
  try {
    localLoading.value = true
    localError.value = ''

    if (!contextSchoolId.value || !contextPeriodId.value || !classroomAssignment.value || !props.classroomId) {
      throw new Error('Informations manquantes pour activer le module')
    }

    // Activer le module
    await dashboardClassroomStore.setModuleVisibility(
      contextSchoolId.value,
      contextPeriodId.value,
      classroomAssignment.value.id,
      moduleId,
      props.classroomId
    )

    // Recharger les données après activation
    await dashboardClassroomStore.fetchDashboardClassroomAssignments(true)
  } catch (err) {
    localError.value = err instanceof Error ? err.message : "Erreur lors de l'activation du module"
    console.error('Error activating module:', err)
  } finally {
    localLoading.value = false
  }
}

function handleViewResults(cardId: string | number) {
  console.log(`View results for card ${cardId}`)
  // Navigation vers la page des résultats de la classe
  router.push({
    name: 'dashboard-group-progress',
    params: {
      schoolId: props.schoolId,
      classroomId: props.classroomId
    }
  })
}

function handleViewContent(cardId: string | number) {
  console.log(`View content for card ${cardId}`)

  // Diagnostic D1
  if (cardId === 'D1') {
    const title = QuizType.label(QuizType.Values.DIAG_START)
    if (dashboardClassroomDiagnosticsByType.value[QuizType.Values.DIAG_START].length === 0) {
      dashboardClassroomStore.showToast({
        severity: 'info',
        summary: `${title} non disponible`,
        detail: `Le ${title} sera disponible une fois publié.`,
        life: 5000,
      })
      return
    }
    const blocks = Object.fromEntries(dashboardClassroomDiagnosticsByType.value[QuizType.Values.DIAG_START]
      .map((qv, index) => [`Partie ${index + 1}`, qv])) as Record<string, QuizVersion> ?? {}
    openQuizVersionDialog(title, blocks)
  }

  // Diagnostic D2
  if (cardId === 'D2') {
    const title = QuizType.label(QuizType.Values.DIAG_END)
    if (dashboardClassroomDiagnosticsByType.value[QuizType.Values.DIAG_END].length === 0) {
      dashboardClassroomStore.showToast({
        severity: 'info',
        summary: `${title} non disponible`,
        detail: `Le ${title} sera disponible une fois publié.`,
        life: 5000,
      })
      return
    }
    const blocks = Object.fromEntries(dashboardClassroomDiagnosticsByType.value[QuizType.Values.DIAG_END]
      .map((qv, index) => [`Partie ${index + 1}`, qv])) as Record<string, QuizVersion> ?? {}
    openQuizVersionDialog(title, blocks)
  }


  // Récupérer les informations du module
  const module = classroomAssignment.value?.assignedModules.find(
    (m) => m.learningModuleId === cardId
  )

  if (module) {
    // Navigation vers la page de contenu du module
    const route = router.resolve({
      name: 'naboo-content-module',
      params: {
        courseId: module.learningCourseId,
        moduleId: module.learningModuleId
      }
    })
    window.open(route.href, '_blank')
  }
}

// LIFECYCLE
onMounted(async () => {
  if (contextSchoolId.value && contextPeriodId.value) {
    try {
      // Charger d'abord les assignments
      await dashboardClassroomStore.fetchDashboardClassroomAssignments()

      // Si un assignment existe, charger l'état des diagnostics
      if (dashboardClassroomAssignments.value) {
        await dashboardClassroomStore.fetchDashboardClassroomDiagnosticState()
      }
    } catch (err) {
      console.error('Error loading data:', err)
    }
  }
})

// WATCHERS
watch([contextSchoolId, contextPeriodId], async ([newSchoolId, newPeriodId]) => {
  if (newSchoolId && newPeriodId) {
    try {
      // Charger d'abord les assignments
      await dashboardClassroomStore.fetchDashboardClassroomAssignments(true)

      // Si un assignment existe, charger l'état des diagnostics
      if (dashboardClassroomAssignments.value) {
        await dashboardClassroomStore.fetchDashboardClassroomDiagnosticState()
      }
    } catch (err) {
      console.error('Error loading data:', err)
    }
  }
})

// Les paramètres de l'établissement ne sont accessibles qu'aux administrateurs (NABOO-768)
async function openSchoolAssignments() {
  if (schoolStore.allSchools.length === 0) await schoolStore.fetchAllSchools()
  const school = schoolStore.allSchools.find((s) => s.id === props.schoolId)
  if (!school) return

  schoolStore.selectSchool(school)
  await router.push({
    name: 'school',
    params: { schoolSlug: school.slug },
    query: { tab: 'Gestion des parcours' }
  })
}
</script>

<template>
  <c-base-block title="Progression du groupe">
    <!-- Message d'erreur (seulement si erreur ET qu'un assignment existe) -->
    <p-message v-if="hasError && classroomAssignment" severity="error" class="mb-4 w-full">
      {{ errorMessage }}
    </p-message>

    <!-- État de chargement -->
    <div v-if="isDataLoading" class="p-4">
      <p-skeleton width="100%" height="200px" class="mb-4" />
      <p-skeleton width="100%" height="200px" />
    </div>

    <!-- Contenu principal -->
    <div v-else class="p-4">
      <!-- Texte d'explication -->
      <div class="mb-6">
        <p class="mb-2 text-sm text-gray-600">
          Le <strong>diagnostic D1</strong>, réalisé en début d'année, évalue les connaissances de
          vos élèves sur toutes les thématiques abordées
          <strong>dans les modules que vous aurez sélectionnés</strong> pour le niveau. Une fois le
          diagnostic réalisé, <strong>activez le module</strong> que vous souhaitez afin de le
          rendre accessible à vos élèves.
        </p>
        <p class="text-sm text-gray-600">
          Publiez le <strong>diagnostic D2</strong> lorsque vos élèves auront réalisé les modules
          que vous souhaitez : il évaluera les connaissances des élèves
          <strong>sur tous les modules réalisés pendant l'année</strong>.
        </p>
      </div>

      <!-- Message si pas d'assignment -->
      <div
        v-if="!classroomAssignment || !classroomAssignment.locked"
        class="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4"
      >
        <div class="flex items-start gap-3">
          <i class="pi pi-info-circle mt-0.5 text-blue-500"></i>
          <div>
            <p class="mb-1 text-sm font-medium text-blue-900">Aucune association de module configuré</p>
            <p class="text-sm text-blue-800">
              Les modules doivent être configurés dans les
              <button
                v-if="isAdmin"
                type="button"
                class="cursor-pointer font-bold underline"
                data-test="open-school-assignments"
                @click="openSchoolAssignments"
              >
                paramètres de l'établissement
              </button>
              <template v-else>paramètres de l'établissement</template>
              pour cette classe avant de pouvoir publier les diagnostics et activer les modules.
            </p>
          </div>
        </div>
      </div>

      <!-- Grille des cartes (diagnostics + modules) - affichée seulement si assignment existe -->
      <div
        v-else
        class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      >
        <c-dashboard-module-card
          v-for="card in cards"
          :key="`${card.type}_${card.id}`"
          :card="card"
          :loading="localLoading"
          @activate-diagnostic="handleActivateDiagnostic"
          @activate-module="handleActivateModule"
          @view-results="handleViewResults"
          @view-content="handleViewContent"
        />
      </div>
    </div>
  </c-base-block>
</template>
